import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DateField } from './DateField';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { roleLabel } from './attributionApi';

function Choice({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Row({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}

export function PartyPicker({ parties, value, onChange, testId }) {
  const options = parties.map((p) => ({ value: p.id, label: `${p.name} · ${roleLabel(p.role)}` }));
  return <Choice value={value} onChange={onChange} placeholder="Select a person…" options={options} testId={testId} />;
}

function FormDialog({ open, title, onClose, onSubmit, busy, testId, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testId} className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy} data-testid={`${testId}-submit`}>{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddRelationshipDialog({ open, onClose, onSave, roles, parties, partners = [], busy }) {
  const [f, setF] = useState({ role: '', user_id: '', external: '', partner: '', effective_from: '', notes: '' });
  const externalOk = (roles.external_allowed || []).includes(f.role);
  const submit = () => onSave({
    role: f.role,
    user_id: f.external || f.partner ? undefined : f.user_id || undefined,
    external_party: f.partner ? { affiliate_id: f.partner } : f.external ? { name: f.external } : undefined,
    effective_from: f.effective_from || undefined,
    notes: f.notes || undefined,
  });
  return (
    <FormDialog open={open} title="Assign merchant relationship" onClose={onClose} onSubmit={submit} busy={busy} testId="add-relationship-dialog">
      <Row label="Role">
        <Choice value={f.role} onChange={(v) => setF({ ...f, role: v })} placeholder="Select a role…" testId="add-relationship-role"
                options={(roles.roles || []).map((r) => ({ value: r, label: roleLabel(r) }))} />
      </Row>
      <Row label="Platform user"><PartyPicker parties={parties} value={f.user_id} onChange={(v) => setF({ ...f, user_id: v, external: '' })} testId="add-relationship-user" /></Row>
      {externalOk && partners.length > 0 && (
        <Row label="…or an existing referral partner">
          <Choice value={f.partner} onChange={(v) => setF({ ...f, partner: v, user_id: '', external: '' })} placeholder="Select a partner…" testId="add-relationship-partner"
                  options={partners.map((p) => ({ value: p.affiliate_id, label: `${p.name}${p.company ? ` · ${p.company}` : ''}` }))} />
        </Row>
      )}
      {externalOk && (
        <Row label="…or a new outside party (not added to your organisation)">
          <Input value={f.external} onChange={(e) => setF({ ...f, external: e.target.value, user_id: '', partner: '' })} placeholder="e.g. Tommy Referrals" data-testid="add-relationship-external" />
        </Row>
      )}
      <Row label="Effective from (optional)"><DateField value={f.effective_from} onChange={(v) => setF({ ...f, effective_from: v })} testId="add-relationship-effective" /></Row>
      <Row label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="add-relationship-notes" /></Row>
    </FormDialog>
  );
}

export function ReasonDialog({ open, title, onClose, onSave, parties, busy, needsParty, dateLabel, testId, kinds, allowExternal }) {
  const [f, setF] = useState({ user_id: '', external: '', reason: '', date: '', kind: 'transfer' });
  const submit = () => onSave(f);
  return (
    <FormDialog open={open} title={title} onClose={onClose} onSubmit={submit} busy={busy} testId={testId}>
      {kinds && (
        <Row label="Type of change">
          <Choice value={f.kind} onChange={(v) => setF({ ...f, kind: v })} testId={`${testId}-kind`} options={[
            { value: 'transfer', label: 'Transfer — effective from a date, history kept' },
            { value: 'correction', label: 'Correction — original was wrong, kept for audit' },
          ]} />
        </Row>
      )}
      {needsParty && <Row label="New holder"><PartyPicker parties={parties} value={f.user_id} onChange={(v) => setF({ ...f, user_id: v, external: '' })} testId={`${testId}-user`} /></Row>}
      {needsParty && allowExternal && (
        <Row label="…or an outside party">
          <Input value={f.external} onChange={(e) => setF({ ...f, external: e.target.value, user_id: '' })} data-testid={`${testId}-external`} />
        </Row>
      )}
      {!(kinds && f.kind === 'correction') && (
        <Row label={`${dateLabel} (optional — defaults to now)`}><DateField value={f.date} onChange={(v) => setF({ ...f, date: v })} testId={`${testId}-date`} /></Row>
      )}
      <Row label="Reason (required, recorded in the audit trail)">
        <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid={`${testId}-reason`} />
      </Row>
    </FormDialog>
  );
}

export function SetAttributionDialog({ open, onClose, onSave, parties, busy, target }) {
  const [f, setF] = useState({ originating_user_id: '', referring_party_name: '', reason: '' });
  const submit = () => onSave({ ...f, referring_party_name: f.referring_party_name || undefined, reason: f.reason || undefined });
  return (
    <FormDialog open={open} title={target?.inheritedFrom ? `Override attribution · ${target.label}` : `Set attribution · ${target?.label || ''}`} onClose={onClose} onSubmit={submit} busy={busy} testId="set-attribution-dialog">
      {target?.inheritedFrom && (
        <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2" data-testid="set-attribution-override-note">
          Currently inherits {target.inheritedFrom}. Saving creates an override for this {roleLabel(target.scope)} only.
        </p>
      )}
      <Row label="Originating agent"><PartyPicker parties={parties} value={f.originating_user_id} onChange={(v) => setF({ ...f, originating_user_id: v })} testId="set-attribution-user" /></Row>
      <Row label="Referring party (optional, outside your org)"><Input value={f.referring_party_name} onChange={(e) => setF({ ...f, referring_party_name: e.target.value })} data-testid="set-attribution-referrer" /></Row>
      <Row label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="set-attribution-reason" /></Row>
    </FormDialog>
  );
}
